import { useContext, useEffect } from 'react'

import {
  AGC_FARMER_ID,
  CLIENT_NAME_COOKIE,
  LAT_LNG_COOKIE_NAME,
  NAVIGATION_FROM_APP_CODE,
  ONE_CLICK_APP_SOURCE,
  SUB_CLIENTS_SOURCE,
} from '@/constants/common'
import { UserContext } from '@/context/UserProvider'
import { APP_CODE_TYPE } from '@/models/Auth'
import CustomEventTypes from '@/utils/analytics/customEventTypes'
import Mixpanel from '@/utils/analytics/mixpanel'
import { trackProductEvent } from '@/utils/analytics/trackProductEvent'
import {
  buildUserDetails,
  mergeSuperProperties,
  setSuperPropertiesStore,
} from '@/utils/analytics/userProperties'
import { withSessionUtmFanOut } from '@/utils/analytics/utmParams'
import {
  getCookieValue,
  isSessionStorageAvailable,
  isValidJSON,
} from '@/utils/helper'
import getClientCode from '@/utils/helpers/getClientCode'
import { runWhenMoengageReady } from '@/utils/moengage'

const useTriggerEvents = () => {
  const { user } = useContext(UserContext)
  const latLong = getCookieValue(LAT_LNG_COOKIE_NAME)
  const clientCode =
    (typeof window !== 'undefined' &&
      isSessionStorageAvailable(window) &&
      sessionStorage?.getItem(ONE_CLICK_APP_SOURCE)) ||
    getCookieValue(CLIENT_NAME_COOKIE) ||
    getClientCode(getCookieValue(NAVIGATION_FROM_APP_CODE) as APP_CODE_TYPE)

  const agcFarmerId = getCookieValue(AGC_FARMER_ID)

  const subClientSource = getCookieValue(SUB_CLIENTS_SOURCE)

  useEffect(() => {
    if (!user) return
    const userDetails = buildUserDetails(user, clientCode, agcFarmerId)
    setSuperPropertiesStore(userDetails)
    Mixpanel.setUser(userDetails)
    // Profile fields via add_user_attribute; identity via loginEvent (add_unique_user_id/add_mobile).
    runWhenMoengageReady(() => {
      Object.entries(userDetails).forEach(([key, value]) => {
        if (value === undefined || value === null) return
        Moengage.add_user_attribute(key, value)
      })
    })
  }, [user, agcFarmerId, clientCode])

  const SERVICE_EVENTS = [
    CustomEventTypes.SERVICE_LANDING_PAGE_VISITED,
    CustomEventTypes.SERVICE_BOOKING_INITIATED,
    CustomEventTypes.SERVICE_PACKAGE_SELECTED,
    CustomEventTypes.SERVICE_MANDATORY_FIELD_ADDED,
    CustomEventTypes.SERVICE_MANDATORY_FIELDS_SUBMITTED,
    CustomEventTypes.SERVICE_ADDRESS_ADDED,
    CustomEventTypes.SERVICE_USER_CLICKS_TO_BOOK_NOW,
    CustomEventTypes.SERVICE_USER_SELECTS_PAYMENT_METHOD,
    CustomEventTypes.PRODUCT_SEARCH_INITIATED,
    CustomEventTypes.USER_LANDED_ON_PRODUCT_DETAIL_PAGE,
    CustomEventTypes.USER_LANDED_ON_COMMERCE_PLATFORM_HOME,
    CustomEventTypes.USER_LANDED_ON_PRODUCT_LISTING_PAGE,
    CustomEventTypes.USER_APPLIED_FILTER_ON_PRODUCT_LISTING_PAGE,
    CustomEventTypes.ADD_TO_CART,
    CustomEventTypes.ORDER_CREATED,
    CustomEventTypes.BUY_NOW,
    CustomEventTypes.OTP_TRIGGERED,
    CustomEventTypes.SELECT_ADDRESS,
    CustomEventTypes.ADD_NEW_ADDRESS_2,
    CustomEventTypes.ADD_NEW_ADDRESS_1,
    CustomEventTypes.CHANGE_ADDRESS,
    CustomEventTypes.CONFIRM_ADDRESS,
    CustomEventTypes.PLACE_CTA_ORDER_CLICKED,
    CustomEventTypes.PAYMENT_MODE_SELECTED,
    CustomEventTypes.FARMER_ORDER_PLACED,
  ] as const

  type ServiceEventType = (typeof SERVICE_EVENTS)[number]

  function captureServiceEvent(
    eventType: CustomEventTypes,
    eventParams: Record<string, any>,
    extraParams: Record<string, any> = {},
  ) {
    if (
      window.fbq != null &&
      SERVICE_EVENTS.includes(eventType as ServiceEventType)
    ) {
      fbq('trackCustom', eventType, {
        ...mergeSuperProperties({ ...eventParams, ...extraParams }),
      })
    }
  }

  const trackCustomEvent = (
    eventType: CustomEventTypes,
    params?: string,
    productName?: string,
  ) => {
    if (typeof window === 'undefined') return

    let extraParams: Record<string, any> = {}
    if (params && isValidJSON(params)) {
      extraParams = { ...JSON.parse(params) }
    }

    let eventParams: Record<string, string> = {
      page_location: window.location?.href ?? '',
      client_src: clientCode,
      AppSource: clientCode,
      sub_client_source: subClientSource,
      is_ondc: window.location?.pathname?.includes('/ondc') ? '1' : '0',
      latLong: latLong,
    }
    let eventName = `${eventType}`

    if (params) {
      eventParams = { ...eventParams, event_params: params }
    }
    if (productName) {
      eventName = `${eventType}/${productName}`
    }

    const { mergedEventParams, mergedExtraParams } = withSessionUtmFanOut(
      eventParams,
      extraParams,
    )

    trackProductEvent(eventName, mergedEventParams, mergedExtraParams)
    captureServiceEvent(eventType, mergedEventParams, mergedExtraParams)
  }

  return {
    trackCustomEvent,
  }
}

export default useTriggerEvents
